import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActions,
  Fade,
  Stack,
  TextField,
  Chip,
  InputAdornment,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Avatar,
  useMediaQuery,
  Tooltip,
  Divider,
  CardMedia,
  Zoom,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Slide,
  LinearProgress,
} from "@mui/material";
import { useTheme, alpha, styled, keyframes } from "@mui/material/styles";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Work as WorkIcon,
  Movie as MovieIcon,
  Handshake as HandshakeIcon,
  Campaign as CampaignIcon,
  Visibility as VisibilityIcon,
  Clear as ClearIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  HelpOutline as HelpIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  VideoLibrary as VideoLibraryIcon,
  Announcement as AnnouncementIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { useCreatorAuth } from "../../../Hooks/creator-use-auth";
import API from "../../../API";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreatorCampaignPitchedDialog from "./creatorcomponents/creatorCampaignPitchedDialog";
import { getOfferFromCampaign } from "../../../Utils/constants";
import ChatModalCreator from "../../../Components/ChatComponents/creatorchatmodal";

const dummyPhotoLinks = [
  "https://storage.googleapis.com/blitzccmc.appspot.com/1/287262.jpg",
  "https://storage.googleapis.com/blitzccmc.appspot.com/1/wp2468668.jpg",
  "https://storage.googleapis.com/blitzccmc.appspot.com/1/eev9l01j44ab1.jpg",
];

// Transition for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const COLLAB_THEME = {
  primary: "#7b2a2a",
  primaryDark: "#662323",
  accent: "#cb534f",
  controlBg: "#f8f2f1",
};

// Pulse animation for the bulletin icon
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(203, 83, 79, 0.55);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(203, 83, 79, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(203, 83, 79, 0);
  }
`;

// ---------- Styled Components ----------
const EnhancedCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
  border: "1px solid",
  borderColor: alpha(theme.palette.text.primary, 0.08),
  background: theme.palette.background.paper,
  overflow: "hidden",
  boxShadow: `0 18px 45px ${alpha(theme.palette.common.black, 0.04)}`,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 24px 60px ${alpha(theme.palette.common.black, 0.08)}`,
    borderColor: alpha(theme.palette.text.primary, 0.16),
  },
}));

const HeaderCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: 0,
  padding: theme.spacing(2.5, 0),
  marginBottom: 0,
  position: "relative",
  overflow: "hidden",
  borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
  "&::before": {
    content: "none",
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2, 0),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 700,
  letterSpacing: "0.01em",
  padding: theme.spacing(1.1, 2.4),
  boxShadow: "none",
  transition: "background-color 180ms ease, border-color 180ms ease, transform 180ms ease",
  "&.MuiButton-contained": {
    backgroundColor: COLLAB_THEME.primary,
    color: "#fff",
  },
  "&.MuiButton-outlined": {
    borderColor: alpha(theme.palette.text.primary, 0.18),
  },
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "none",
  },
  "&.MuiButton-contained:hover": {
    backgroundColor: COLLAB_THEME.primaryDark,
  },
}));

const MetricCard = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha("#fff", 0.12)} 0%, ${alpha("#fff", 0.08)} 100%)`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2.5),
  border: `1px solid ${alpha("#fff", 0.15)}`,
  backdropFilter: "blur(10px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha("#fff", 0.18)} 0%, ${alpha("#fff", 0.12)} 100%)`,
    transform: "translateY(-2px)",
    boxShadow: `0 4px 16px ${alpha("#000", 0.15)}`,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const statusColors = {
    active: { bg: "#121826", color: "#fff" },
    launched: { bg: "#121826", color: "#fff" },
    pending: { bg: "#efe7d2", color: "#6d4f09" },
    draft: { bg: "#efe7d2", color: "#6d4f09" },
    completed: { bg: "#e5edf7", color: "#27405f" },
    archived: { bg: theme.palette.grey[200], color: theme.palette.grey[700] },
    offered: { bg: "#eef1ff", color: "#3151b7" },
    current: { bg: "#121826", color: "#fff" },
    counter: { bg: "#efe7d2", color: "#6d4f09" },
    open: { bg: "#e5edf7", color: "#27405f" },
    applied: { bg: "#eef1ff", color: "#3151b7" },
    accepted: { bg: "#121826", color: "#fff" },
  };
  const colorConfig = statusColors[status] || {
    bg: theme.palette.grey[100],
    color: theme.palette.text.secondary,
  };
  return {
    backgroundColor: colorConfig.bg,
    color: colorConfig.color,
    fontWeight: 800,
    fontSize: "0.65rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    borderRadius: 999,
    height: 24,
    "& .MuiChip-label": {
      px: 1,
    },
  };
});

const SupervisorInlineCard = styled(Paper)(({ theme }) => ({
  borderRadius: 18,
  padding: theme.spacing(2),
  background: "#f7f8fb",
  border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
  boxShadow: "none",
  color: theme.palette.text.primary,
}));

// Styled IconButton with pulse animation
const PulsingIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "shouldPulse",
})(({ theme, shouldPulse }) => ({
  bgcolor: alpha(COLLAB_THEME.accent, 0.14),
  color: COLLAB_THEME.primary,
  "&:hover": { bgcolor: alpha(COLLAB_THEME.accent, 0.22) },
  ...(shouldPulse && { animation: `${pulse} 2s infinite` }),
}));

// Minimal Ranking Display Component
const MinimalRankingDisplay = ({ rankingData, isLoading }) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "fit-content",
          maxWidth: "100%",
          px: 1.4,
          py: 0.65,
          borderRadius: 999,
          background: "#fff",
          border: `1px solid ${alpha("#121826", 0.08)}`,
          boxShadow: `0 10px 24px ${alpha("#121826", 0.05)}`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
            Loading score...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!rankingData) return null;

  const { overall_score, tier, percentile } = rankingData;
  const scoreAccent = "#0f766e";
  const scoreAccentAlt = "#334155";
  const topPercent = typeof percentile === "number"
    ? Math.max(0, Math.min(100, 100 - percentile))
    : null;
  const topPercentLabel = topPercent !== null
    ? Number.isInteger(topPercent) ? topPercent : topPercent.toFixed(1)
    : null;

  const getTierColor = (tier) => {
    const colors = {
      elite: "#FFD700",
      "rising star": "#9C27B0",
      established: "#2196F3",
      emerging: "#4CAF50",
      newcomer: "#757575",
    };
    return colors[tier?.toLowerCase()] || colors.newcomer;
  };

  const getTierIcon = (tier) => {
    if (tier?.toLowerCase() === "elite") return <TrophyIcon sx={{ fontSize: 20 }} />;
    return <StarIcon sx={{ fontSize: 20 }} />;
  };

  return (
    <Box
      sx={{
        width: "fit-content",
        maxWidth: "100%",
        px: { xs: 1, sm: 1.15 },
        py: 0.7,
        borderRadius: 999,
        background: `linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)`,
        border: `1px solid ${alpha(scoreAccentAlt, 0.1)}`,
        boxShadow: `0 12px 28px ${alpha(scoreAccentAlt, 0.055)}`,
      }}
    >
      <Stack
        direction="row"
        spacing={{ xs: 0.65, sm: 0.85 }}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.35,
            flexShrink: 0,
            px: 1,
            py: 0.45,
            borderRadius: 999,
            bgcolor: alpha(scoreAccent, 0.08),
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 950,
              letterSpacing: "-0.03em",
              color: scoreAccent,
              lineHeight: 1,
              fontSize: { xs: "1rem", sm: "1.08rem" },
            }}
          >
            {overall_score}
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(scoreAccentAlt, 0.68), fontWeight: 800 }}>
            / 100
          </Typography>
        </Box>

        <Chip
          icon={getTierIcon(tier)}
          label={tier}
          size="small"
          sx={{
            height: 28,
            fontSize: "0.68rem",
            fontWeight: 900,
            textTransform: "capitalize",
            bgcolor: alpha(scoreAccentAlt, 0.07),
            color: scoreAccentAlt,
            flexShrink: 0,
            "& .MuiChip-icon": { color: alpha(scoreAccentAlt, 0.72), fontSize: 14 },
            "& .MuiChip-label": { px: 0.9 },
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 0.9,
            py: 0.7,
            borderRadius: 999,
            bgcolor: alpha(scoreAccentAlt, 0.045),
            flexShrink: 0,
          }}
        >
          <LinearProgress
            variant="determinate"
            value={percentile}
            sx={{
              width: { xs: 62, sm: 96 },
              height: 5,
              borderRadius: 999,
              bgcolor: alpha(scoreAccentAlt, 0.1),
              "& .MuiLinearProgress-bar": {
                background: `linear-gradient(90deg, ${scoreAccent} 0%, #14b8a6 100%)`,
                borderRadius: 999,
              },
            }}
          />
        </Box>

        {topPercentLabel !== null && (
          <Typography
            variant="caption"
            sx={{
              color: alpha(scoreAccentAlt, 0.78),
              fontWeight: 900,
              whiteSpace: "nowrap",
              display: { xs: "none", sm: "block" },
              px: 0.55,
              "& strong": {
                color: scoreAccent,
              },
            }}
          >
            Top <strong>{topPercentLabel}%</strong>
          </Typography>
        )}

        <Tooltip
          title="Your marketplace ranking helps brands discover you"
          placement="bottom-start"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: { offset: [0, 8] },
                },
              ],
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              color: alpha(scoreAccentAlt, 0.72),
              width: 24,
              height: 24,
              bgcolor: alpha(scoreAccentAlt, 0.07),
              flexShrink: 0,
              "&:hover": { bgcolor: alpha(scoreAccentAlt, 0.11) },
            }}
          >
            <InfoIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const CheckIsPitched = (campaign, username) => {
  const creator = campaign.creators.find((c) => c.id === username);
  return (
    (creator?.status === undefined ||
      creator?.status === "Pitched" ||
      creator?.status === "Counter") &&
    campaign.campaign_status === "Launched"
  );
};

// ---------- Pending Items Dialog ----------
const PendingItemsDialog = ({
  open,
  onClose,
  pendingData,
  isLoading,
  navigate,
  theme,
  isMobile,
}) => {
  if (!pendingData) return null;
  const { summary, pending_items } = pendingData;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? "100vh" : "90vh",
          pb: isMobile ? "calc(110px + env(safe-area-inset-bottom, 0px))" : 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 40, height: 40 }}>
            <AnnouncementIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Pending Action Items
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.total_campaigns_with_pending} campaign
              {summary.total_campaigns_with_pending !== 1 ? "s" : ""} need attention
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    borderRadius: 2,
                    textAlign: "center",
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: theme.palette.warning.main }}
                  >
                    {summary.total_pending_videos}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pending Videos
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    borderRadius: 2,
                    textAlign: "center",
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: theme.palette.info.main }}
                  >
                    {summary.total_pending_timeline_events}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Timeline Items
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {/* Campaign List */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "text.primary" }}>
              Campaigns:
            </Typography>
            <Stack spacing={2}>
              {pending_items.map((campaign) => (
                <Card
                  key={campaign.campaign_id}
                  variant="outlined"
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: theme.shadows[4],
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                  onClick={() => {
                    navigate(`/creatorconnect/campaigns/${campaign.campaign_id}`);
                    onClose();
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                        {campaign.campaign_name}
                      </Typography>
                      {campaign.ideal_duedate && (
                        <Chip
                          icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                          label={new Date(campaign.ideal_duedate).toLocaleDateString()}
                          size="small"
                          color="default"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    {campaign.pending_videos.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                        >
                          <VideoLibraryIcon
                            sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }}
                          />
                          {campaign.pending_videos.length} Video
                          {campaign.pending_videos.length !== 1 ? "s" : ""} Pending
                        </Typography>
                        <List dense sx={{ pt: 0 }}>
                          {campaign.pending_videos.slice(0, 2).map((video) => (
                            <ListItem key={video.video_id} sx={{ py: 0.5, px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={`${video.platform} - ${video.type}`}
                                secondary={video.reasons.join(", ")}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontSize: "0.85rem",
                                }}
                                secondaryTypographyProps={{
                                  variant: "caption",
                                  fontSize: "0.75rem",
                                }}
                              />
                            </ListItem>
                          ))}
                          {campaign.pending_videos.length > 2 && (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
                              +{campaign.pending_videos.length - 2} more
                            </Typography>
                          )}
                        </List>
                      </Box>
                    )}
                    {campaign.pending_timeline_events.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                        >
                          <AssignmentIcon
                            sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }}
                          />
                          {campaign.pending_timeline_events.length} Timeline Item
                          {campaign.pending_timeline_events.length !== 1 ? "s" : ""}
                        </Typography>
                        <List dense sx={{ pt: 0 }}>
                          {campaign.pending_timeline_events.slice(0, 2).map((event) => (
                            <ListItem key={event.event_id} sx={{ py: 0.5, px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                {event.is_overdue ? (
                                  <WarningIcon color="error" sx={{ fontSize: 16 }} />
                                ) : (
                                  <ScheduleIcon color="info" sx={{ fontSize: 16 }} />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={event.objective}
                                secondary={
                                  event.projected_date
                                    ? `Due: ${new Date(event.projected_date).toLocaleDateString()}${
                                        event.is_overdue ? " (Overdue)" : ""
                                      }`
                                    : "No due date"
                                }
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontSize: "0.85rem",
                                }}
                                secondaryTypographyProps={{
                                  variant: "caption",
                                  fontSize: "0.75rem",
                                  color: event.is_overdue ? "error" : "text.secondary",
                                }}
                              />
                            </ListItem>
                          ))}
                          {campaign.pending_timeline_events.length > 2 && (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
                              +{campaign.pending_timeline_events.length - 2} more
                            </Typography>
                          )}
                        </List>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Enhanced Header with Integrated Ranking ----------
const EnhancedHeader = ({
  searchQuery,
  setSearchQuery,
  onRefresh,
  isMobile,
  pendingData,
  pendingLoading,
  onOpenPending,
  onOpenChat,
  hasSupervisor,
  supervisor,
  supAvatar,
  supName,
  rankingData,
  rankingLoading,
  username,
}) => {
  const theme = useTheme();

  const totalPending = pendingData?.summary
    ? pendingData.summary.total_pending_videos + pendingData.summary.total_pending_timeline_events
    : 0;
  const hasPendingItems = pendingData?.summary?.total_campaigns_with_pending > 0;

  return (
    <HeaderCard elevation={0}>
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Stack
            direction="column"
            spacing={0}
            alignItems="flex-start"
            sx={{ minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  letterSpacing: "-0.03em",
                  fontSize: { xs: "1.35rem", md: "1.65rem" },
                  lineHeight: 1.1,
                }}
              >
                Collaborations
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  mt: 0.4,
                  fontSize: { xs: "0.78rem", md: "0.9rem" },
                }}
              >
                Manage your brand partnerships and casting calls
              </Typography>
              <Box sx={{ mt: 1.25 }}>
                <MinimalRankingDisplay rankingData={rankingData} isLoading={rankingLoading} />
              </Box>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "space-between", md: "flex-end" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {hasPendingItems && (
                  <Tooltip title="View pending action items">
                    <PulsingIconButton onClick={onOpenPending} shouldPulse={hasPendingItems}>
                      <Badge
                        badgeContent={totalPending}
                        max={99}
                        sx={{
                          "& .MuiBadge-badge": {
                            bgcolor: COLLAB_THEME.accent,
                            color: "#fff",
                            fontWeight: 800,
                          },
                        }}
                      >
                        <AnnouncementIcon />
                      </Badge>
                    </PulsingIconButton>
                  </Tooltip>
                )}

                {hasSupervisor && (
                  <Tooltip title="Chat with your account manager">
                    <IconButton
                      onClick={onOpenChat}
                      sx={{
                        color: "text.primary",
                        bgcolor: COLLAB_THEME.controlBg,
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                        "&:hover": { bgcolor: alpha(COLLAB_THEME.accent, 0.14) },
                      }}
                    >
                      <ChatBubbleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              <ActionButton
                variant="contained"
                onClick={onRefresh}
                fullWidth={false}
                startIcon={<RefreshIcon />}
                sx={{ px: isMobile ? 1.75 : 2.4 }}
              >
                Refresh Data
              </ActionButton>
            </Stack>
          </Stack>
        </Stack>

        {hasSupervisor && supervisor && (
          <SupervisorInlineCard sx={{ mt: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar src={supAvatar} alt={supName} sx={{ width: 40, height: 40 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={800} noWrap>
                    {supName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Your Blitz Bestie ✨
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                {supervisor.email && (
                  <Chip icon={<EmailIcon />} label={supervisor.email} variant="outlined" size="small" sx={{ maxWidth: 180 }} />
                )}
                {supervisor.phone_number && (
                  <Chip icon={<PhoneIcon />} label={supervisor.phone_number} variant="outlined" size="small" sx={{ maxWidth: 160 }} />
                )}
                <ActionButton
                  variant="contained"
                  startIcon={<ChatBubbleOutlineIcon />}
                  onClick={onOpenChat}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Chat Now
                </ActionButton>
              </Stack>
            </Stack>
          </SupervisorInlineCard>
        )}
      </Container>
    </HeaderCard>
  );
};

// ---------- Enhanced Collaboration Card with Image ----------
const EnhancedCollaborationCard = ({ item, username, onClick, index }) => {
  const theme = useTheme();
  const isCounter =
    item.type === "campaign" &&
    item.creators?.find((c) => c.id === username)?.status === "Counter";

  const isCastingCallApplied = item.type === "casting_call" && item.creator_status === "APPLIED";
  const isCastingCallAccepted = item.type === "casting_call" && item.creator_status === "ACCEPTED";

  const getTypeIcon = (type) => {
    switch (type) {
      case "campaign": return <CampaignIcon />;
      case "partnership": return <HandshakeIcon />;
      case "casting_call": return <MovieIcon />;
      default: return <WorkIcon />;
    }
  };
  const getTypeColor = (type) => {
    switch (type) {
      case "campaign": return theme.palette.secondary.main;
      case "partnership": return theme.palette.primary.main;
      case "casting_call": return theme.palette.info.main;
      default: return theme.palette.grey[600];
    }
  };
  
  const getButtonLabel = (item) => {
    if (item.type === "campaign") return "View Campaign";
    if (item.type === "partnership") return "View Partnership";
    if (item.type === "casting_call") {
      if (item.creator_status === "ACCEPTED") return "Manage Collaboration";
      if (item.creator_status === "APPLIED") return "View Application";
      return "View Details";
    }
    return "View";
  };
  
  const getStatusForChip = (item) => {
    if (item.type === "campaign") return item.campaign_status?.toLowerCase();
    if (item.type === "partnership") return item.status?.toLowerCase();
    if (item.type === "casting_call") {
      return item.creator_status?.toLowerCase() || item.status?.toLowerCase();
    }
    return "pending";
  };

  const imageUrl = dummyPhotoLinks[item.id % dummyPhotoLinks.length];
  const title = item.name || item.brief || `#${item.id}`;
  const campaignOffer = item.type === "campaign" ? getOfferFromCampaign(item, username) : null;

  return (
    <Zoom in timeout={200 + index * 50}>
      <EnhancedCard
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderColor: isCounter ? alpha(theme.palette.warning.main, 0.6) : alpha(theme.palette.text.primary, 0.08),
          cursor: (isCastingCallApplied || !item.is_clickable) ? "default" : "pointer",
          opacity: isCastingCallApplied ? 0.85 : 1,
          "&:hover": {
            opacity: 1,
          },
        }}
        onClick={() => {
          if (!isCastingCallApplied && item.is_clickable !== false) {
            onClick(item);
          }
        }}
      >
        <Box sx={{ position: "relative", bgcolor: "#101722" }}>
          <CardMedia 
            component="img" 
            height="190"
            image={imageUrl} 
            alt={title}
            sx={{ 
              objectFit: "cover",
              filter: isCastingCallApplied ? "grayscale(65%)" : "saturate(0.8) contrast(1.02)",
              opacity: 0.72,
            }} 
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(180deg, ${alpha("#02040a", 0.15)} 0%, ${alpha("#02040a", 0.72)} 100%)`,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              right: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Chip
              label={
                item.type === "campaign"
                  ? "Campaign"
                  : item.type === "partnership"
                  ? "Partnership"
                  : "Casting Call"
              }
              size="small"
              sx={{
                borderRadius: 999,
                fontSize: "0.65rem",
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                bgcolor: alpha(getTypeColor(item.type), 0.85),
                color: "white",
                backdropFilter: "blur(12px)",
                "& .MuiChip-label": { px: 1 },
              }}
            />
            <StatusChip
              label={
                item.type === "campaign"
                  ? item.campaign_status
                  : item.type === "partnership"
                  ? item.status
                  : (item.creator_status || item.status).charAt(0).toUpperCase() + (item.creator_status || item.status).slice(1)
              }
              status={getStatusForChip(item)}
              size="small"
            />
          </Box>
          <Box
            sx={{
              position: "absolute",
              left: 18,
              right: 18,
              bottom: 18,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 999,
                display: "grid",
                placeItems: "center",
                color: "#fff",
                bgcolor: alpha("#fff", 0.12),
                border: `1px solid ${alpha("#fff", 0.18)}`,
                backdropFilter: "blur(12px)",
              }}
            >
              {getTypeIcon(item.type)}
            </Box>
            {campaignOffer && (
              <Typography
                variant="h6"
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  textShadow: `0 2px 12px ${alpha("#000", 0.35)}`,
                }}
              >
                {campaignOffer}
              </Typography>
            )}
          </Box>
          {isCounter && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: alpha(theme.palette.warning.main, 0.9),
                color: "white",
                py: 0.5,
                px: 1.5,
                textAlign: "center",
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                Deal Pending Client Approval
              </Typography>
            </Box>
          )}
          {isCastingCallApplied && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: alpha(theme.palette.warning.main, 0.9),
                color: "white",
                py: 0.5,
                px: 1.5,
                textAlign: "center",
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                Pending Selection
              </Typography>
            </Box>
          )}
        </Box>

        <CardContent sx={{ p: 2.75, flexGrow: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                fontSize: "1.08rem",
                letterSpacing: "-0.02em",
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {campaignOffer && (
              <Typography
                variant="subtitle1"
                sx={{
                  display: { xs: "none", sm: "block" },
                  fontWeight: 900,
                  color: "text.primary",
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                }}
              >
                {campaignOffer}
              </Typography>
            )}
          </Box>

          <Stack spacing={2}>
            {item.type === "campaign" && (
              <Box
                sx={{
                  bgcolor: "#f7f8fb",
                  p: 1.5,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Your Offer
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: theme.palette.text.primary }}>
                  {campaignOffer}
                </Typography>
              </Box>
            )}

            {item.type === "partnership" && item.description && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.5,
                }}
              >
                {item.description}
              </Typography>
            )}

            {item.type === "casting_call" && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        bgcolor: "#f7f8fb",
                        p: 1.5,
                        borderRadius: 3,
                        textAlign: "center",
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
                        Budget Range
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, color: theme.palette.success.main }}
                      >
                        ${item.budget_min} - ${item.budget_max}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        bgcolor: "#f7f8fb",
                        p: 1.5,
                        borderRadius: 3,
                        textAlign: "center",
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
                        Platforms
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }} noWrap>
                        {item.platforms?.join(", ") || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {item.application_message && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.5 }}>
                      Your Application:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        fontStyle: "italic",
                      }}
                    >
                      "{item.application_message}"
                    </Typography>
                  </Box>
                )}

                {item.applied_at && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ScheduleIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary">
                      Applied {new Date(item.applied_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}

                {isCastingCallAccepted && item.organizer_count > 0 && (
                  <Box
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        Collaboration Active
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VideoLibraryIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <Typography variant="caption" fontWeight={600}>
                        {item.organizer_count} File Organizer{item.organizer_count !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    {item.primary_organizer && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Primary: {item.primary_organizer.name} ({item.primary_organizer.file_count} files)
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Stack>
        </CardContent>

        <CardActions sx={{ px: 2.75, pb: 2.75, pt: 0 }}>
          {isCastingCallApplied ? (
            <ActionButton
              variant="outlined"
              fullWidth
              disabled
              sx={{
                borderColor: theme.palette.grey[300],
                color: theme.palette.text.secondary,
              }}
            >
              Awaiting Selection
            </ActionButton>
          ) : (
            <ActionButton
              variant="text"
              fullWidth
              startIcon={<VisibilityIcon />}
              onClick={(e) => {
                e.stopPropagation();
                if (!isCastingCallApplied && item.is_clickable !== false) {
                  onClick(item);
                }
              }}
              sx={{
                justifyContent: "space-between",
                color: "#3151b7",
                bgcolor: alpha("#3151b7", 0.06),
                px: 1.5,
                "&:hover": {
                  bgcolor: alpha("#3151b7", 0.1),
                },
              }}
            >
              {getButtonLabel(item)}
            </ActionButton>
          )}
        </CardActions>
      </EnhancedCard>
    </Zoom>
  );
};

const LoadingSkeleton = () => {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f7f8fc" }}>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          height: 96,
          mb: 3,
        }}
      />
      <Container maxWidth="xl">
        <Box sx={{ height: 72, bgcolor: "background.paper", borderRadius: 4, mb: 3 }} />
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "none", border: 1, borderColor: "divider" }}>
                <Box sx={{ height: 190, bgcolor: "#121826" }} />
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ height: 28, bgcolor: "grey.200", borderRadius: 1, mb: 2 }} />
                  <Box sx={{ height: 60, bgcolor: "grey.100", borderRadius: 1, mb: 2 }} />
                  <Box sx={{ height: 40, bgcolor: "grey.200", borderRadius: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const CreatorCollaborations = () => {
  const { creatorToken } = useCreatorAuth();
  const username = creatorToken?.creator_user?.username;
  const token = creatorToken?.token;
  const creator = creatorToken?.creator_user?.creator;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [collaborations, setCollaborations] = useState([]);
  const [currentFilter, setCurrentFilter] = useState("Current");
  const [currentCampaign, setCurrentCampaign] = useState(-1);
  const [openPitch, setOpenPitch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  /* ——— RANKING QUERY ——— */
  const {
    data: rankingData,
    isLoading: rankingLoading,
    refetch: refetchRanking,
  } = useQuery(
    ["creatorMarketplaceRanking", username],
    () => API.creators.getMarketplaceRanking(username),
    {
      enabled: !!username,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  /* ——— SUPERVISOR LOGIC ——— */
  const supUserId = creator?.superviser;
  const {
    data: supervisorRaw,
    isLoading: loadingSup,
    error: supErr,
  } = useQuery(
    ["supervisorProfile", supUserId],
    () => API.users.fetchPublicDetails(supUserId),
    {
      enabled: !!supUserId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 24,
      cacheTime: 1000 * 60 * 60 * 24,
    }
  );

  const supervisor = useMemo(() => {
    if (!supervisorRaw) return null;
    return supervisorRaw.user ?? supervisorRaw;
  }, [supervisorRaw]);

  /* ——— CHAT MODAL STATE ——— */
  const [chatOpen, setChatOpen] = useState(false);
  const openChat = () => supUserId && setChatOpen(true);

  /* ——— PENDING ITEMS QUERY ——— */
  const {
    data: pendingData,
    isLoading: pendingLoading,
    refetch: refetchPending,
  } = useQuery(["campaignsPending", username], () => API.creatorConnect.fetchCampaignsToDo(), {
    enabled: !!username,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (!pendingLoading && !hasAutoOpened && pendingData?.summary?.total_campaigns_with_pending > 0) {
      setPendingDialogOpen(true);
      setHasAutoOpened(true);
    }
  }, [pendingData, pendingLoading, hasAutoOpened]);

  // Query functions
  const fetchCampaigns = async () => {
    // if (!token) return [];
    const response = await API.creatorConnect.campaigns();
    return Array.isArray(response) ? response : response?.data || [];
  };
  const fetchPartnerships = async () => {
    const response = await API.creatorConnect.getPartnerships();
    return Array.isArray(response) ? response : response?.data || [];
  };
  
  const fetchCastingCalls = async () => {
    const response = await API.castingCalls.getCreatorApplications({ status: 'all' });
    return Array.isArray(response?.casting_calls) ? response.casting_calls : [];
  };

  // React Query hooks
  const { isLoading: campaignsLoading, data: campaigns = [], refetch: refetchCampaigns } = useQuery(
    ["campaigns", "creator", username],
    fetchCampaigns
  );
  const { isLoading: partnershipsLoading, data: partnerships = [], refetch: refetchPartnerships } =
    useQuery(["creator", "partnerships"], fetchPartnerships);
  const { isLoading: castingCallsLoading, data: castingCalls = [], refetch: refetchCastingCalls } =
    useQuery(["castingCalls", "creator"], fetchCastingCalls);

  useEffect(() => {
    const campaignsWithType = campaigns.map((campaign) => ({ ...campaign, type: "campaign" }));
    const partnershipsWithType = partnerships.map((partnership) => ({ ...partnership, type: "partnership" }));
    const castingCallsWithType = castingCalls.map((castingCall) => ({
      ...castingCall,
      type: "casting_call",
      status: castingCall.creator_status?.toLowerCase() || castingCall.status,
      isApplied: castingCall.creator_status === "APPLIED",
    }));
    const combinedCollaborations = [
      ...campaignsWithType,
      ...partnershipsWithType,
      ...castingCallsWithType,
    ];
    setCollaborations(combinedCollaborations);
  }, [campaigns, partnerships, castingCalls]);

  const prevOpenPitchRef = useRef(openPitch);
  useEffect(() => {
    if (prevOpenPitchRef.current && !openPitch) refetchCampaigns();
    prevOpenPitchRef.current = openPitch;
  }, [openPitch, refetchCampaigns]);

  const loading = campaignsLoading || partnershipsLoading || castingCallsLoading;

  useEffect(() => {
    const currentTab = searchParams.get("tab") || "Current";
    if (currentTab !== currentFilter) setCurrentFilter(currentTab);
  }, [searchParams]);

  const handleTabChange = (_, newValue) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", newValue);
    setSearchParams(newParams);
    setCurrentFilter(newValue);
  };

  const filteredCollaborations = useMemo(() => {
    return collaborations.filter((item) => {
      let tabMatch = true;
      if (item.type === "campaign") {
        const creator = item.creators.find((c) => c.id === username);
        switch (currentFilter) {
          case "All":
            tabMatch = !["Archived", "Draft", "DELETED"].includes(item.campaign_status);
            break;
          case "Current":
            tabMatch =
              creator?.status &&
              !["Declined", "Dropped", "Pitched", "Counter"].includes(creator.status) &&
              item.campaign_status === "Launched";
            break;
          case "Offered":
            tabMatch =
              (creator?.status === undefined || ["Pitched", "Counter"].includes(creator?.status)) &&
              item.campaign_status === "Launched";
            break;
          case "Completed":
            tabMatch =
              creator?.status &&
              !["Declined", "Dropped", "Pitched", "Counter"].includes(creator.status) &&
              item.campaign_status === "Completed";
            break;
          default:
            tabMatch = true;
        }
      } else if (item.type === "partnership") {
        switch (currentFilter) {
          case "All":
            tabMatch = !["Archived", "Draft", "DELETED"].includes(item.status);
            break;
          case "Current":
            tabMatch = item.status === "Active";
            break;
          case "Offered":
            tabMatch = ["pending", "Draft"].includes(item.status);
            break;
          case "Completed":
            tabMatch = item.status === "Completed";
            break;
          default:
            tabMatch = true;
        }
      } else if (item.type === "casting_call") {
        switch (currentFilter) {
          case "All":
            tabMatch = true;
            break;
          case "Current":
            tabMatch = item.creator_status === "ACCEPTED";
            break;
          case "Offered":
            tabMatch = item.creator_status === "APPLIED";
            break;
          case "Completed":
            tabMatch = false;
            break;
          default:
            tabMatch = true;
        }
      }

      const searchMatch =
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.brief?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.id?.toString().includes(searchQuery);

      return tabMatch && searchMatch;
    });
  }, [collaborations, currentFilter, username, searchQuery]);

  const getPriority = (item) => {
    if (item.type === "partnership") {
      if (item.status === "Active") return 0;
      if (["pending", "Draft"].includes(item.status)) return 1;
      if (item.status === "Completed") return 2;
      return 3;
    }
    if (item.type === "casting_call") {
      if (item.creator_status === "ACCEPTED") return 0;
      if (item.creator_status === "APPLIED") return 1;
      return 3;
    }
    if (item.type === "campaign") {
      const creator = item.creators.find((c) => c.id === username);
      const accepted =
        creator?.status && !["Declined", "Dropped", "Pitched", "Counter"].includes(creator.status);
      if (accepted && item.campaign_status === "Launched") return 0;
      if ((!creator?.status || ["Pitched", "Counter"].includes(creator?.status)) && item.campaign_status === "Launched")
        return 1;
      if (accepted && item.campaign_status === "Completed") return 2;
      return 3;
    }
    return 3;
  };

  const sortedCollaborations = useMemo(() => {
    return [...filteredCollaborations].sort((a, b) => {
      const priorityDiff = getPriority(a) - getPriority(b);
      if (priorityDiff !== 0) return priorityDiff;
      const dateA = new Date(a.updated_at || a.created_at || a.applied_at || 0);
      const dateB = new Date(b.updated_at || b.created_at || b.applied_at || 0);
      return dateB - dateA;
    });
  }, [filteredCollaborations]);

  const offeredCount = useMemo(() => {
    return collaborations.reduce((count, item) => {
      if (item.type === "campaign") {
        const creator = item.creators.find((c) => c.id === username);
        if (
          (!creator?.status || ["Pitched", "Counter"].includes(creator?.status)) &&
          item.campaign_status === "Launched"
        ) {
          count++;
        }
      } else if (item.type === "partnership") {
        if (["pending", "Draft"].includes(item.status)) count++;
      } else if (item.type === "casting_call") {
        if (item.creator_status === "APPLIED") count++;
      }
      return count;
    }, 0);
  }, [collaborations, username]);

  const handleItemClick = (item) => {
    if (item.type === "campaign") {
      if (CheckIsPitched(item, username)) {
        setCurrentCampaign(item.id);
        setOpenPitch(true);
      } else {
        navigate(`/creatorconnect/campaigns/${item.id}`);
      }
    } else if (item.type === "partnership") {
      navigate(`/creatorconnect/partnerships/${item.id}`);
    } else if (item.type === "casting_call") {
      if (item.manage_url && item.creator_status === "ACCEPTED") {
        navigate(item.manage_url);
      } else {
        navigate(`/castingcall/${item.id}/manage`);
      }
    }
  };

  const handleClosePitch = useCallback(() => setOpenPitch(false), []);

  const handleRefresh = () => {
    refetchCampaigns();
    refetchPartnerships();
    refetchCastingCalls();
    refetchPending();
    refetchRanking();
    setSnackbar({
      open: true,
      message: "Data refreshed successfully!",
      severity: "success",
    });
  };

  const supName =
    supervisor ? `${supervisor.first_name ?? ""} ${supervisor.last_name ?? ""}`.trim() || supervisor.username : "—";
  const supAvatar = supervisor?.phphref || "";

  if (loading) return <LoadingSkeleton />;

  return (
    <Box sx={{ bgcolor: "#f7f8fc", minHeight: "100vh" }}>
      {/* Enhanced Header with Integrated Ranking */}
      <EnhancedHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onRefresh={handleRefresh}
        isMobile={isMobile}
        pendingData={pendingData}
        pendingLoading={pendingLoading}
        onOpenPending={() => setPendingDialogOpen(true)}
        onOpenChat={openChat}
        hasSupervisor={!!supUserId}
        supervisor={supervisor}
        supAvatar={supAvatar}
        supName={supName}
        rankingData={rankingData}
        rankingLoading={rankingLoading}
        username={username}
      />

      <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 3.5 }, pb: 5 }}>
        <Stack spacing={1.75} sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterListIcon sx={{ color: COLLAB_THEME.primary, fontSize: 16 }} />
            <Typography
              variant="overline"
              sx={{
                color: COLLAB_THEME.primary,
                fontWeight: 900,
                letterSpacing: "0.24em",
                lineHeight: 1,
              }}
            >
              Collaboration Overview
            </Typography>
          </Box>

          <EnhancedCard sx={{ p: { xs: 1.25, md: 1.5 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Tabs
                value={currentFilter}
                onChange={handleTabChange}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons={isMobile ? "auto" : false}
                TabIndicatorProps={{
                  sx: {
                    height: "100%",
                    borderRadius: 999,
                    bgcolor: COLLAB_THEME.primary,
                    zIndex: 0,
                    transition: "left 260ms cubic-bezier(0.4, 0, 0.2, 1), width 260ms cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                }}
                sx={{
                  minHeight: 0,
                  position: "relative",
                  "& .MuiTabs-scroller": {
                    position: "relative",
                  },
                  "& .MuiTabs-flexContainer": {
                    gap: { xs: 0.5, md: 1 },
                    position: "relative",
                    zIndex: 1,
                  },
                  "& .MuiTab-root": {
                    minHeight: 0,
                    minWidth: { xs: 86, md: 106 },
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: "0.82rem", md: "0.88rem" },
                    color: "text.secondary",
                    px: { xs: 1.5, md: 2.2 },
                    py: 1,
                    transition: "color 220ms ease",
                    "&.Mui-selected": {
                      color: "#fff",
                    },
                  },
                }}
              >
                <Tab label="All" value="All" />
                <Tab label="Current" value="Current" />
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Badge
                        badgeContent={offeredCount}
                        overlap="rectangular"
                        sx={{
                          "& .MuiBadge-badge": {
                            bgcolor: COLLAB_THEME.accent,
                            color: "#fff",
                            fontSize: "0.65rem",
                            fontWeight: 800,
                          },
                        }}
                      >
                        <HandshakeIcon sx={{ fontSize: 17 }} />
                      </Badge>
                      Offered
                    </Box>
                  }
                  value="Offered"
                />
                <Tab label="Completed" value="Completed" />
              </Tabs>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: 1, md: "auto" } }}>
                <TextField
                  fullWidth
                  placeholder="Search collaborations by name, description, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                          sx={{ color: "text.secondary" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: { md: 320 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 999,
                      fontSize: "0.88rem",
                      backgroundColor: COLLAB_THEME.controlBg,
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: alpha(theme.palette.text.primary, 0.12),
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: alpha(COLLAB_THEME.accent, 0.5),
                      },
                    },
                  }}
                />
              </Stack>
            </Stack>
          </EnhancedCard>
        </Stack>

        {/* Search Results Header */}
        {(searchQuery || currentFilter !== "All") && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                color: theme.palette.text.primary,
                mb: 0.3,
                fontSize: { xs: "1.05rem", sm: "1.2rem" },
              }}
            >
              {searchQuery ? `Search results for "${searchQuery}"` : `${currentFilter} Collaborations`}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
              {sortedCollaborations.length} items found
            </Typography>
          </Box>
        )}

        {/* Collaboration Cards */}
        <Box sx={{ mb: 4 }}>
          {sortedCollaborations.length > 0 ? (
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {sortedCollaborations.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={`${item.type}-${item.id}`}>
                  <EnhancedCollaborationCard
                    item={item}
                    username={username}
                    onClick={handleItemClick}
                    index={index}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <EnhancedCard sx={{ p: { xs: 4, md: 6 }, textAlign: "center", bgcolor: "#fff" }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 4,
                  display: "grid",
                  placeItems: "center",
                  mx: "auto",
                  mb: 2,
                  bgcolor: "#f2f4fa",
                  color: "text.secondary",
                }}
              >
                <BusinessIcon sx={{ fontSize: 38 }} />
              </Box>
              <Typography variant="h6" color="text.primary" gutterBottom fontWeight={900}>
                No collaborations found
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {searchQuery
                  ? "Try adjusting your search terms"
                  : `No ${currentFilter === "All" ? "" : currentFilter.toLowerCase() + " "}collaborations available at this time`}
              </Typography>
            </EnhancedCard>
          )}
        </Box>
      </Container>

      {/* Pending Items Dialog */}
      <PendingItemsDialog
        open={pendingDialogOpen}
        onClose={() => setPendingDialogOpen(false)}
        pendingData={pendingData}
        isLoading={pendingLoading}
        navigate={navigate}
        theme={theme}
        isMobile={isMobile}
      />

      {/* Campaign Pitch Dialog */}
      <CreatorCampaignPitchedDialog
        openDialog={openPitch}
        handleCloseDialog={handleClosePitch}
        campaignId={currentCampaign}
      />

      {/* Chat Modal */}
      <ChatModalCreator
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        recipientUserId={supUserId}
      />

      {/* Enhanced Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 3, fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreatorCollaborations;
